/**
 * RecordingView Component
 *
 * Main component for the recording app that supports both Dyte and LiveKit providers.
 * Uses the adapter pattern to provide a unified interface for different meeting SDKs.
 */

import React from 'react';
import { RealtimeKitProvider, useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RealtimeKitRecording } from '@cloudflare/realtimekit-recording-sdk';
import { RtkSpinner } from '@cloudflare/realtimekit-react-ui';
import { LiveKitRoom } from '@livekit/components-react';
import { Socket } from 'socket.io-client';
import { useSearchParams } from 'react-router-dom';
import { useMeetingProvider } from 'contexts/MeetingProviderContext';
import useSocketConnection from 'hooks/useSocketConnection';
import { AllContent, Participants, TimeComponent } from 'components';
import { SidePanel } from 'components/SidePanel';
import { IRecordingView } from 'types';
import { useAppDispatch, useOverlayPanels, useResponsiveLayout } from 'hooks';
import { resetClassNotesState } from 'store/slice/notesHtmlData.slice';
import MeetingAudio from 'components/MeetingAudio';
import AutoEndRecording from 'hooks/useAutoEndRecording';
import LiveKitReconnect from 'hooks/useLiveKitReconnect';
import RecorderStudentLayers from 'hooks/useRecorderStudentLayers';
import {
  AUTO_END_RECORDING_ENABLED,
  AUTO_END_RECORDING_MINUTES,
} from 'configs/config';
import { datadogLogs } from '@datadog/browser-logs';

interface DyteError extends Error {
  code?: string | number;
  message: string;
}

interface LiveKitRecordingViewProps extends IRecordingView {
  livekitToken: string;
  wsUrl: string;
}

/**
 * Common UI component used by both providers
 * Contains the layout and shared components
 */
const RecordingViewContent: React.FC<{
  socket: Socket | null | undefined;
  classId: string | null;
  elementRef: React.RefObject<HTMLDivElement>;
  zoom?: number;
  source?: string | null;
  studentId?: string | null;
}> = ({ socket, classId, elementRef, zoom = 1, source, studentId }) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const forTutorManager = searchParams.get('forTM')?.toLowerCase() === 'true';
  const overlayPanelsEnabled = useOverlayPanels();
  // Narrow parent embed: slide on top, participants as a strip underneath.
  const { isStacked } = useResponsiveLayout();

  React.useEffect(() => {
    if (classId) {
      dispatch(resetClassNotesState(classId));
    }
  }, [classId, dispatch]);

  return (
    <div
      style={{
        backgroundColor: '#000000',
        height: `${100 / zoom}vh`,
        overflow: 'hidden',
      }}
      ref={elementRef}
      className={
        isStacked
          ? 'flex flex-col gap-2 p-2'
          : 'grid grid-cols-12 grid-rows-[1fr] gap-2 p-2'
      }
    >
      {/* Main content area - adjust columns based on source */}
      <div
        className={
          isStacked
            ? 'flex flex-col gap-2 flex-1 min-h-0'
            : `${source === 'parent' ? 'col-span-10' : 'col-span-9'} flex flex-col gap-2 h-full max-h-[calc(100%-16px)]`
        }
      >
        {/* Slides/Main content container with proper height calculation */}
        <div className='relative flex-1 min-h-0 overflow-hidden'>
          {/* TimeComponent positioned on the right side over the slide - hide for parent source */}
          {source !== 'parent' && (
            <div className='absolute top-4 right-4 z-50'>
              <TimeComponent />
            </div>
          )}

          {/* Main content takes full height of this container */}
          <div className='h-full'>
            <AllContent socket={socket ?? undefined} />
          </div>
        </div>

        {/* Bottom panel section with fixed height - AI Assistant - opt-in via
            ?panels=true; always hidden for parent source and tutor manager.
            Unmounting it is what stops the suggestions/nudges polling. */}
        {overlayPanelsEnabled && source !== 'parent' && !forTutorManager && (
          <div className='h-44 flex-shrink-0'>
            <div className='bg-black rounded-lg h-full p-2 flex flex-col overflow-hidden border border-gray-800'>
              <div className='font-bold text-white mb-2 px-2 flex-shrink-0 flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  {/* AI Sparkle Icon */}
                  <div className='relative'>
                    <div className='w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center'>
                      <svg width='18' height='18' viewBox='0 0 24 24' fill='white'>
                        {/* Main sparkle - four-pointed star */}
                        <path d='M12 0L14.4 9.6L24 12L14.4 14.4L12 24L9.6 14.4L0 12L9.6 9.6L12 0Z' />
                        {/* Small sparkle top-right */}
                        <path
                          d='M18 2L18.9 4.1L21 5L18.9 5.9L18 8L17.1 5.9L15 5L17.1 4.1L18 2Z'
                          transform='scale(0.5)'
                          opacity='0.8'
                        />
                        {/* Small sparkle bottom-left */}
                        <path
                          d='M6 14L6.9 16.1L9 17L6.9 17.9L6 20L5.1 17.9L3 17L5.1 16.1L6 14Z'
                          transform='scale(0.5)'
                          opacity='0.8'
                        />
                      </svg>
                    </div>
                    {/* Subtle glow effect */}
                    <div className='absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg blur-md opacity-40'></div>
                  </div>

                  {/* Title */}
                  <div>
                    <div className='text-xs text-gray-400 uppercase tracking-wider'>UPRIO's</div>
                    <div className='text-base'>Tutor AI Assistant</div>
                  </div>
                </div>

                {/* Live indicator */}
                <div className='flex items-center gap-2 text-xs text-gray-400'>
                  <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
                  <span>Active</span>
                </div>
              </div>
              <div className='flex-1 min-h-0 overflow-hidden'>
                <SidePanel panelType='suggestions' />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Participants area - right rail when wide, bottom strip when stacked */}
      <div
        className={
          isStacked
            ? // Portrait frames have height to spare, so the strip grows with the
              // viewport. Tiles divide the width evenly, so this height is what
              // actually sizes them - capped so the slide keeps the bulk of the frame.
              'h-[30vh] min-h-[140px] max-h-[300px] shrink-0 overflow-hidden'
            : `${source === 'parent' ? 'col-span-2' : 'col-span-3'} h-full max-h-[calc(100%-16px)] overflow-hidden`
        }
      >
        <Participants
          socket={socket}
          source={source}
          studentId={studentId}
          forTutorManager={forTutorManager}
          isStacked={isStacked}
        />
      </div>
    </div>
  );
};

/**
 * Dyte-specific implementation
 */
const DyteRecordingView: React.FC<IRecordingView & { zoom?: number }> = ({ authToken, classId, zoom, source, studentId }) => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  const [client, initClient] = useRealtimeKitClient();
  const socket = useSocketConnection({ classId });

  // Initialize DyteMeeting
  React.useEffect(() => {
    if (!authToken) {
      return;
    }
    async function setupDyteMeeting() {
      try {
        const meetingObj = await initClient({
          authToken,
          defaults: {
            audio: false,
            video: false,
          },
        });

        if (meetingObj) {
          // Per RealtimeKit docs the recording SDK inits AFTER initMeeting and
          // BEFORE joinRoom. devMode lets the recorder run outside Cloudflare's
          // recording-bot infra (e.g. opened manually for local testing) — without
          // it the SDK waits on the orchestrator websocket. Non-fatal so joinRoom
          // still runs if it fails.
          try {
            const recordingSDK = new RealtimeKitRecording({
              devMode: import.meta.env.DEV,
            });
            await recordingSDK.init(meetingObj);
          } catch (recErr) {
            datadogLogs.logger.error(
              'RealtimeKit recording SDK init failed',
              { label: 'rtk-recording-init', source: 'recording-app' },
              recErr as Error,
            );
          }

          await meetingObj.joinRoom();

        }
      } catch (error: unknown) {
        const dyteError = error as DyteError;
        datadogLogs.logger.error(
          'Error initializing Dyte meeting',
          {
            label: 'dyte-error',
            source: 'recording-app',
            error: dyteError?.message,
            code: dyteError?.code,
          },
          error as Error,
        );
      }
    }
    setupDyteMeeting();
  }, [authToken]);

  if (!client) {
    return (
      <div className='flex flex-1 justify-center items-center h-full'>
        <RtkSpinner />
      </div>
    );
  }

  return (
    <RealtimeKitProvider value={client}>
      <RecordingViewContent socket={socket} classId={classId} elementRef={elementRef} zoom={zoom} source={source} studentId={studentId} />
      <MeetingAudio meeting={client} />
    </RealtimeKitProvider>
  );
};

/**
 * LiveKit-specific implementation
 * NOTE: This will be fully implemented in Phase 3
 */
const LiveKitRecordingView: React.FC<LiveKitRecordingViewProps & { zoom?: number }> = ({
  livekitToken,
  wsUrl,
  classId,
  zoom,
  source,
  studentId,
}) => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  const socket = useSocketConnection({ classId });
  const [searchParams] = useSearchParams();
  const forTutorManager = searchParams.get('forTM')?.toLowerCase() === 'true';

  // Only the real class recorder auto-ends. The parent-filtered view and the
  // TM live view are separate LiveKit sessions that must never stop the class
  // egress.
  const autoEndEnabled =
    AUTO_END_RECORDING_ENABLED && source !== 'parent' && !forTutorManager;

  // Flipped once the room is unrecoverable, which releases the auto-end
  // watchdog's hold on the empty-room countdown.
  const [reconnectExhausted, setReconnectExhausted] = React.useState(false);

  return (
    <LiveKitRoom
      serverUrl={wsUrl}
      token={livekitToken}
      connect={true}
      audio={false}
      video={false}
      onError={(error) => {
        datadogLogs.logger.error(
          'Error in LiveKit room',
          {
            label: 'livekit-error',
            source: 'recording-app',
            error: error.message,
          },
          error,
        );
      }}
    >
      <RecordingViewContent socket={socket} classId={classId} elementRef={elementRef} zoom={zoom} source={source} studentId={studentId} />
      <MeetingAudio meeting={null} />
      <RecorderStudentLayers />
      <LiveKitReconnect
        serverUrl={wsUrl}
        token={livekitToken}
        logContext={{ classId, view: source ?? (forTutorManager ? 'tm' : 'recorder') }}
        onGiveUp={() => setReconnectExhausted(true)}
      />
      <AutoEndRecording
        classId={classId}
        enabled={autoEndEnabled}
        emptyThresholdMs={AUTO_END_RECORDING_MINUTES * 60 * 1000}
        livekitToken={livekitToken}
        reconnectExhausted={reconnectExhausted}
      />
    </LiveKitRoom>
  );
};

/**
 * Main RecordingView component that routes to the appropriate provider
 */
const RecordingView: React.FC<IRecordingView & Partial<LiveKitRecordingViewProps> & { zoom?: number }> = (props) => {
  const { provider } = useMeetingProvider();
  const { zoom } = props;

  if (provider === 'livekit') {
    const { livekitToken, wsUrl } = props;

    if (!livekitToken || !wsUrl) {
      datadogLogs.logger.error(
        'LiveKit provider selected but missing required props',
        {
          label: 'livekit-config-error',
          source: 'recording-app',
          hasToken: !!livekitToken,
          hasWsUrl: !!wsUrl,
        },
      );
      return (
        <div className='flex items-center justify-center flex-1 h-full text-white'>
          <div className='text-center'>
            <p className='text-red-500 mb-2'>Configuration Error</p>
            <p className='text-sm'>LiveKit token or server URL is missing</p>
          </div>
        </div>
      );
    }

    return (
      <LiveKitRecordingView
        {...props}
        livekitToken={livekitToken}
        wsUrl={wsUrl}
        zoom={zoom}
      />
    );
  }

  // Default to Dyte
  return <DyteRecordingView {...props} zoom={zoom} />;
};

export default RecordingView;
